import { flg } from "./ui/logging"
/**
 * Frontend Sentry Error Reporting
 *
 * Consistent error reporting for all frontend apps.
 * Mirrors the backend reportErrorToSentry from @aix/apiutils.
 *
 * Usage:
 *   import { reportErrorToSentry } from "@aix/webutils"
 *
 *   try {
 *     await riskyOperation()
 *   } catch (err) {
 *     reportErrorToSentry(err, "checkout", { userId, priceId })
 *     toast.error("Something went wrong")
 *   }
 */

// Lazy load Sentry to avoid bundling issues
// Using any to avoid compile-time dependency on @sentry/nextjs types
// (not all workspaces have @sentry/nextjs installed)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let Sentry: any = null

const loadSentry = async () => {
  if (Sentry) return Sentry
  try {
    // Try @sentry/react first (Vite/React apps)
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - @sentry/react resolved by host app, not a webutils dep
    // eslint-disable-next-line @aix/no-inline-dynamic-imports -- @sentry/react is resolved by host app at runtime, not a webutils dependency
    Sentry = await import(/* @vite-ignore */ "@sentry/react")
    return Sentry
    // eslint-disable-next-line @aix/no-exception-swallowing -- intentional fallback: @sentry/react unavailable, try @sentry/nextjs next
  } catch {
    // Not a Vite/React app, try Next.js
  }
  try {
    // Fallback to @sentry/nextjs (Next.js apps)
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - @sentry/nextjs resolved by host app, not a webutils dep
    // eslint-disable-next-line @aix/no-inline-dynamic-imports -- @sentry/nextjs is resolved by host app at runtime, not a webutils dependency
    Sentry = await import(/* @vite-ignore */ "@sentry/nextjs")
    return Sentry
  } catch {
    // Sentry not available (e.g., in tests)
    return null
  }
}

// Initialize on first use
void loadSentry()

/**
 * Errors that are typically user-caused or expected, not bugs.
 * These will still be logged but marked as handled.
 */
const ignoredErrorPatterns = [
  /network error/i,
  /aborted/i,
  /user cancelled/i,
  /user denied/i,
  /exceeded the maximum number of declines/i, // Stripe card decline
  /card was declined/i,
  /insufficient funds/i,
  /expired card/i,
]

const shouldIgnoreError = (error: Error | string): boolean => {
  const message = typeof error === "string" ? error : error?.message || ""
  return ignoredErrorPatterns.some((pattern) => pattern.test(message))
}

export interface SentryErrorContext {
  /** Tags for filtering in Sentry (indexed, searchable) */
  tags?: Record<string, string>
  /** Extra data for context (not indexed) */
  extra?: Record<string, unknown>
  /** User info */
  user?: {
    id?: string
    email?: string
  }
}

/**
 * Report an error to Sentry with context.
 *
 * @param error - The error to report
 * @param flow - A unique identifier for the flow/component (e.g., "checkout", "signup")
 * @param context - Additional context (tags, extra data, user info)
 *
 * @example
 * ```typescript
 * try {
 *   await processPayment()
 * } catch (err) {
 *   reportErrorToSentry(err, "payment", {
 *     tags: { component: "CheckoutModal", action: "submit" },
 *     extra: { priceId, userId },
 *   })
 *   toast.error("Payment failed")
 * }
 * ```
 */
export const reportErrorToSentry = async (
  error: Error | unknown,
  flow?: string,
  context: SentryErrorContext = {},
): Promise<void> => {
  const err = error instanceof Error ? error : new Error(String(error))

  // Always log to console for debugging
  flg.error("[Sentry] Frontend error reported")

  // Check if we should ignore this error
  if (shouldIgnoreError(err)) {
    flg.info("[Sentry] Expected error ignored")
    return
  }

  try {
    const sentry = await loadSentry()
    if (!sentry) {
      flg.warn("[Sentry] Not available - error not reported to Sentry")
      return
    }

    sentry.withScope((scope) => {
      // Set flow as a tag for easy filtering
      if (flow) {
        scope.setTag("flow", flow)
      }

      // Set custom tags
      if (context.tags) {
        scope.setTags(context.tags)
      }

      // Set extra context
      if (context.extra) {
        scope.setExtras(context.extra)
      }

      // Set user info
      if (context.user) {
        scope.setUser(context.user)
      }

      sentry.captureException(err)
    })
    // eslint-disable-next-line @aix/no-exception-swallowing -- Sentry failure is non-critical; logging is sufficient to avoid masking the original error
  } catch (sentryError) {
    flg.error("[Sentry] Failed to report error")
  }
}

/**
 * Capture a message to Sentry (for non-error events).
 *
 * @param message - The message to capture
 * @param level - Severity level
 * @param context - Additional context
 */
export const reportMessageToSentry = async (
  message: string,
  level: "info" | "warning" | "error" = "info",
  context: SentryErrorContext = {},
): Promise<void> => {
  try {
    const sentry = await loadSentry()
    if (!sentry) return

    sentry.withScope((scope) => {
      scope.setLevel(level)

      if (context.tags) {
        scope.setTags(context.tags)
      }

      if (context.extra) {
        scope.setExtras(context.extra)
      }

      sentry.captureMessage(message)
    })
    // eslint-disable-next-line @aix/no-exception-swallowing -- Sentry failure is non-critical; logging is sufficient
  } catch (err) {
    flg.error("[Sentry] Failed to report message")
  }
}
